import PropTypes from 'prop-types';
import Textarea from "components/form/Textarea/Textarea";
import { useState } from "react";
import Dialog from "components/ui/Dialog/Dialog";
import { useDialog } from "context/DialogContext";
import Button from "components/ui/Button/Button";
import useData from "hooks/useData";
import Input from "components/form/Input/Input";
import Interests from "components/ui/Interests/Interests";
import ImageUpload from "components/form/ImageUpload/ImageUpload";
import useEntity from "hooks/useEntity";
import Autocomplete from "components/form/Autocomplete/Autocomplete";
import Radio from "components/form/Radio/Radio";
import { Link } from "react-router-dom";

const GroupCreateUpdate = ({ id, onCompleted }) => {
  const { hideDialog } = useDialog();
  const { postData, patchData } = useData();
  const [ saving, setSaving ] = useState(false);
  const [ error, setError ] = useState();
  const [ title, setTitle ] = useState('');
  const [ description, setDescription ] = useState('');
  const [ interests, setInterests ] = useState([]);
  const [ imageData, setImageData ] = useState();
  const [ location, setLocation ] = useState();
  const [ locationDescription, setLocationDescription ] = useState();
  const [ isPrivate, setIsPrivate ] = useState(`0`);

  const { entity, isLoading } = useEntity(id, 'group');

  // Set default values once the entity data is loaded.
  if (id && entity && !title) {
    setTitle(entity.title && entity.title.value);
    setDescription(entity.field_description && entity.field_description.value);
    setInterests(entity.field_interests ? entity.field_interests.map(item => item.id) : []);
    setLocation(entity.field_location ? entity.field_location.value : null);
    setLocationDescription(entity.field_location_description ? entity.field_location_description.value : null);
    setIsPrivate(entity.field_group_is_private?.value || `0`);
  }

  const handleRenderLocation = (result) => {
    return result.description;
  }

  const handleSelectLocation = (item) => {
    setLocation(item?.wkt);
    setLocationDescription(item?.description);
  }

  const onSave = async () => {
    setError(null);

    if (!title) {
      setError('Titel is een verplicht veld');
      return;
    }

    if (!description) {
      setError('Het veld "Over de groep" is een verplicht veld');
      return;
    }

    setSaving(true);

    const saveData = {
      ...entity,
      title: { value: title },
      field_description: { value: description },
      field_group_photo: { target_id: imageData && imageData.fid[0].value },
      field_interests: interests.map(id => {
        return { target_id: id }
      }),
      field_location: { value: location },
      field_location_description: { value: location ? locationDescription : null },
      field_group_is_private: { value: isPrivate },
    };

    let response;
    if (entity && entity.id) {
      response = await patchData(entity.id, 'group', saveData);
    } else {
      response = await postData('rest/group', saveData);
    }

    const result = await response.json();

    if (response?.status === 200) {
      hideDialog();
      if (onCompleted) {
        onCompleted(result);
      }
      return;
    }

    setError(result?.message || `Het opslaan is mislukt (${ result?.status })`);

    setSaving(false);
  };

  const form = (
    <>
      <div className={ `dialog__row` }>
        <h3>Naam groep</h3>
        <Input type={ `text` } onChange={ event => setTitle(event.target.value) } value={ title } autoFocus/>
      </div>

      <div className={ `dialog__row` }>
        <h3>Over de groep</h3>
        <Textarea onChange={ event => setDescription(event.target.value) } value={ description }/>
      </div>

      <div className={ `dialog__row` }>
        <h3>Om welke postcode of plaats gaat het? <span className={'heading-subtext'}>(niet verplicht)</span></h3>
        <Autocomplete endpoint={ `rest/geocode` }
                      value={ locationDescription && { description: locationDescription } }
                      placeholder={ `Wat is uw postcode of plaats?` }
                      onRenderResult={ handleRenderLocation }
                      onSelect={ handleSelectLocation }/>
      </div>

      <div className={ `dialog__row` }>
        <h3>Kies bij de groep passende interesses</h3>
        <Interests selection={ interests } onSelectionUpdated={ setInterests }/>
      </div>

      <div className={ `dialog__row` }>
        <ImageUpload title={ `Foto bij de groep` }
                     subtitle={ `(niet verplicht)` }
                     type="node"
                     bundle="group"
                     field="field_group_photo"
                     currentData={ imageData }
                     currentValue={ entity && entity.field_group_photo && entity.field_group_photo.value }
                     onUploaded={ setImageData }/>
      </div>

      <div className={ `dialog__row` }>
        <h3>Open/Gesloten groep</h3>
        <div>
          <Radio label={ `Gesloten (goedkeuring door groepsbeheerder vereist)` }
                 checked={ isPrivate === `1` }
                 group={ 'private' }
                 onChange={ () => setIsPrivate(() => `1`) }
          />
          <Radio label={ `Open (iedereen mag lid worden)` }
                 checked={ isPrivate === `0` }
                 group={ 'private' }
                 onChange={ () => setIsPrivate(() => `0`) }
          />
        </div>
      </div>

      <div className={ 'dialog__row dialog__buttons dialog__buttons--end' }>
        <Button onClick={ onSave } variant={ 'secondary' }>Opslaan</Button>
      </div>
    </>
  );

  const existingGroup = entity?.id;

  return (
    <Dialog title={ existingGroup ? 'Groep aanpassen' : 'Nieuwe groep maken' }
            loading={ isLoading || saving }
            errorMessage={ error }
            warnOnClose={ true }>
      { form }
      {
        !existingGroup &&
        <div className={ `dialog__footnote` }>
          U wordt automatisch beheerder van de nieuwe groep, de groep dient zich te houden aan de <Link to={ '/huisregels' } target="_blank" rel="noopener noreferrer">huisregels</Link>.
        </div>
      }
    </Dialog>
  )
}

GroupCreateUpdate.propTypes = {
  id: PropTypes.number,
  onCompleted: PropTypes.func,
}

export default GroupCreateUpdate;
